/**
 * Named labels (ADR-039): one org workspace's labels — Workspace Controls →
 * Named labels.
 *
 * URL: /workspaces/:holderId/labels and /workspaces/:holderId/labels/:labelId.
 *
 * A named label ("Board", "HR") restricts a document ON TOP OF its tier: only
 * the people on the label see it, each up to their workspace role. Owners and
 * admins see every label here (they govern them) but are on none by default;
 * anyone else sees only the labels they are on. A label's name is never shown
 * to anyone else, and this screen never shows which documents, or how many,
 * carry a label.
 *
 * The server decides every answer; the screen only avoids offering what would
 * be refused.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock, Tag, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  VAULT_NAMED_LABEL_DESCRIPTION_MAX,
  VAULT_NAMED_LABEL_NAME_MAX,
  VAULT_NAMED_LABEL_MANAGER_LOOKBACK_DAYS,
  VAULT_NAMED_LABEL_REQUEST_LAPSE_DAYS,
  type VaultHolderMemberDto,
  type VaultNamedLabelRequestDto,
  type VaultNamedLabelSummaryDto,
} from '@/lib/api';
import { StepUpDialog } from '../components/StepUpDialog';
import { useWorkspaceMembers } from '../hooks/useWorkspaceTeam';
import {
  approveVaultNamedLabelRequest,
  describeNamedLabelError,
  useCanCreateVaultNamedLabel,
  useAskAgainVaultNamedLabelRequest,
  useCreateVaultNamedLabel,
  useMyWorkspaceSeat,
  useRejectVaultNamedLabelRequest,
  useVaultNamedLabelReceipts,
  useVaultNamedLabelRequests,
  useVaultNamedLabels,
  useVaultNamedLabelsInvalidator,
  useWithdrawVaultNamedLabelRequest,
} from '../hooks/useVaultNamedLabels';
import { LabelDetail } from './named-labels/LabelDetail';
import {
  describeRequest,
  isPrivilegedTier,
  lapsesIn,
  namedLabelDetailPath,
  namedLabelsPath,
  personLabel,
  workspaceGroupsPath,
} from './named-labels/shared';

export function NamedLabelsPage() {
  const params = useParams<{ holderId: string; labelId?: string }>();
  const holderId = params.holderId ?? '';
  const labelId = params.labelId ?? null;
  const seat = useMyWorkspaceSeat(holderId);
  const labels = useVaultNamedLabels(holderId || null);
  const canCreate = useCanCreateVaultNamedLabel(holderId || null);
  const members = useWorkspaceMembers(holderId || null);
  const privileged = isPrivilegedTier(seat.role);
  const label = labelId ? labels.data?.find((l) => l.labelId === labelId) : undefined;

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
          <p className="text-sm">
            <Link to="/workspaces" className="text-muted-foreground hover:underline">
              Workspace Controls
            </Link>
            {labelId && (
              <>
                {' / '}
                <Link
                  to={namedLabelsPath(holderId)}
                  className="text-muted-foreground hover:underline"
                >
                  Named labels
                </Link>
              </>
            )}
          </p>
          <h1 className="text-2xl font-bold text-foreground">
            {label ? label.name : 'Named labels'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A named label keeps documents to the people on it, on top of their sensitivity. Owners
            and admins aren’t on a label unless they’re added.
          </p>
          {!labelId && (
            <p className="mt-2 text-sm">
              <Link
                to={workspaceGroupsPath(holderId)}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                Groups
              </Link>{' '}
              <span className="text-muted-foreground">
                — name a team once, like “Board” or “HR”, and put it on a label.
              </span>
            </p>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {seat.isLoading || labels.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : labels.isError ? (
          <p className="text-sm text-destructive">
            {describeNamedLabelError(labels.error, 'Labels could not be loaded.')}
          </p>
        ) : labelId ? (
          label ? (
            <LabelDetail
              holderId={holderId}
              label={label}
              myTier={seat.role}
              myPrincipalId={seat.principalId}
              members={members.data ?? []}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              This label isn’t available — it may have been deleted, or you’re not on it.
            </p>
          )
        ) : (
          <>
            <LabelsListCard
              holderId={holderId}
              labels={labels.data ?? []}
              privileged={privileged}
            />
            {/* Only when the server would accept it: the rollout switch admits a
                workspace's first label, so before that there is no form. */}
            {privileged && canCreate.data === true && <CreateLabelCard holderId={holderId} />}
            <RequestsCard
              holderId={holderId}
              labels={labels.data ?? []}
              members={members.data ?? []}
              myPrincipalId={seat.principalId}
            />
            <FilingsCard holderId={holderId} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The labels
// ---------------------------------------------------------------------------

function LabelsListCard({
  holderId,
  labels,
  privileged,
}: {
  holderId: string;
  labels: readonly VaultNamedLabelSummaryDto[];
  privileged: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="h-4 w-4" aria-hidden="true" />
          Labels
        </CardTitle>
        <CardDescription>
          {privileged
            ? 'Every label in this workspace. You only see a label’s documents if you’re on it.'
            : 'The labels you’re on, and drop boxes you can file documents into.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {labels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {privileged ? 'No labels yet.' : 'You’re not on any labels.'}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {labels.map((l) => (
              <li key={l.labelId} className="px-3 py-2">
                <Link
                  to={namedLabelDetailPath(holderId, l.labelId)}
                  className="flex flex-wrap items-center justify-between gap-2 hover:underline"
                >
                  <span className="flex items-center gap-2 font-medium text-foreground">
                    <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                    {l.name}
                  </span>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {privileged || l.myPlace ? (
                      <span>
                        {l.readerCount} {l.readerCount === 1 ? 'person' : 'people'}
                      </span>
                    ) : (
                      // A drop box shown only to file into: who is on it is not
                      // the filer's to know (the server sends no count).
                      <span>You can file documents into it</span>
                    )}
                    {l.myPlace?.isManager ? (
                      <Badge variant="outline">You manage this</Badge>
                    ) : l.myPlace?.viaGroup ? (
                      <Badge variant="outline">You read this through a group</Badge>
                    ) : l.myPlace ? (
                      <Badge variant="outline">You’re on this</Badge>
                    ) : null}
                    {l.acceptsFromNonReaders && <Badge variant="outline">Drop box</Badge>}
                    {l.deprecatedAt && <Badge variant="secondary">Deprecated</Badge>}
                  </span>
                </Link>
                {l.orphaned && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    No manager — an owner can add themselves to manage it.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CreateLabelCard({ holderId }: { holderId: string }) {
  const create = useCreateVaultNamedLabel(holderId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dropBox, setDropBox] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New label</CardTitle>
        <CardDescription>
          You’ll be on it and manage it. You can add people once it’s made.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              {
                name: name.trim(),
                description: description.trim() || null,
                acceptsFromNonReaders: dropBox,
              },
              {
                onSuccess: (label) => {
                  toast({ title: 'Label created', description: label.name });
                  setName('');
                  setDescription('');
                  setDropBox(false);
                },
                onError: (err) =>
                  toast({
                    title: 'Could not create the label',
                    description: describeNamedLabelError(err),
                    variant: 'destructive',
                  }),
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="named-label-name">Name</Label>
            <Input
              id="named-label-name"
              value={name}
              maxLength={VAULT_NAMED_LABEL_NAME_MAX}
              placeholder="Board"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="named-label-description">Description (optional)</Label>
            <Textarea
              id="named-label-description"
              value={description}
              maxLength={VAULT_NAMED_LABEL_DESCRIPTION_MAX}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={dropBox} onCheckedChange={setDropBox} />
            <span>
              Drop box
              <span className="block text-xs text-muted-foreground">
                Members not on the label can file documents into it but can’t see them afterwards.
              </span>
            </span>
          </label>
          <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create label'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Requests: changes a manager approves, and admins adding themselves
// ---------------------------------------------------------------------------

function RequestsCard({
  holderId,
  labels,
  members,
  myPrincipalId,
}: {
  holderId: string;
  labels: readonly VaultNamedLabelSummaryDto[];
  members: readonly VaultHolderMemberDto[];
  myPrincipalId: string | null;
}) {
  const requests = useVaultNamedLabelRequests(holderId);
  const reject = useRejectVaultNamedLabelRequest(holderId);
  const withdraw = useWithdrawVaultNamedLabelRequest(holderId);
  const askAgain = useAskAgainVaultNamedLabelRequest(holderId);
  const invalidate = useVaultNamedLabelsInvalidator(holderId);
  const [approving, setApproving] = useState<VaultNamedLabelRequestDto | null>(null);
  const approvingWords = approving ? requestWords(approving, labels, members, myPrincipalId) : '';

  const all = requests.requests;
  const pending = all.filter((r) => r.status === 'pending');
  // Who may decide is the server's answer (`mayDecide`, the rule the decision
  // itself applies — the sole manager's own request included), never worked
  // out here from who manages what now.
  const toDecide = pending.filter((r) => r.mayDecide);
  // A declined or lapsed group request is shown while it may be asked again —
  // the server's answer (`mayAskAgain`: still in the group, nothing already
  // waiting for them, and the caller can still add people to it).
  const mine = all.filter(
    (r) =>
      r.requestedByPrincipalId === myPrincipalId &&
      !(r.status === 'pending' && r.mayDecide) &&
      (r.status === 'pending' || r.mayAskAgain === true),
  );

  if (requests.isLoading) return null;
  if (requests.isError) {
    return (
      <p className="text-sm text-destructive">
        {describeNamedLabelError(requests.error, 'Requests could not be loaded.')}
      </p>
    );
  }
  if (mine.length === 0 && toDecide.length === 0 && !requests.hasMore) return null;

  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeNamedLabelError(err), variant: 'destructive' });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Requests</CardTitle>
        <CardDescription>
          Taking a label off a document, moving it to another label, or lowering its sensitivity
          needs a manager of the label to approve. So does someone added to a group that reads a
          label, unless a manager of the label added them. An admin adding themselves needs another
          owner or admin. Requests nobody decides expire after{' '}
          {VAULT_NAMED_LABEL_REQUEST_LAPSE_DAYS} days.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {toDecide.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Waiting for you</h3>
            <ul className="space-y-2">
              {toDecide.map((r) => {
                const own = r.requestedByPrincipalId === myPrincipalId;
                return (
                  <li
                    key={r.requestId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <span>
                      <RequestText
                        request={r}
                        labels={labels}
                        members={members}
                        myPrincipalId={myPrincipalId}
                      />{' '}
                      <span className="text-xs text-muted-foreground">
                        ({lapsesIn(r.lapsesAt)})
                      </span>
                      {own && (
                        <span className="block text-xs text-muted-foreground">
                          Nobody else has managed this label in the last{' '}
                          {VAULT_NAMED_LABEL_MANAGER_LOOKBACK_DAYS} days, so you can approve your
                          own request.
                        </span>
                      )}
                    </span>
                    <span className="flex gap-2">
                      <Button size="sm" onClick={() => setApproving(r)}>
                        Approve
                      </Button>
                      {own ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={withdraw.isPending}
                          onClick={() =>
                            withdraw.mutate(r.requestId, {
                              onSuccess: () => toast({ title: 'Request withdrawn' }),
                              onError: onError('Could not withdraw'),
                            })
                          }
                        >
                          Withdraw
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={reject.isPending}
                          onClick={() =>
                            reject.mutate(r.requestId, {
                              onSuccess: () => toast({ title: 'Declined' }),
                              onError: onError('Could not decline'),
                            })
                          }
                        >
                          Decline
                        </Button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {mine.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Your requests</h3>
            <ul className="space-y-2">
              {mine.map((r) => (
                <li
                  key={r.requestId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                >
                  <span>
                    <RequestText
                      request={r}
                      labels={labels}
                      members={members}
                      myPrincipalId={myPrincipalId}
                    />{' '}
                    <span className="text-xs text-muted-foreground">
                      {r.status === 'pending'
                        ? `Waiting for approval — ${lapsesIn(r.lapsesAt)}.`
                        : r.status === 'rejected'
                          ? 'Declined.'
                          : 'Expired — nobody decided in time.'}
                    </span>
                  </span>
                  {r.status === 'pending' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={withdraw.isPending}
                      onClick={() =>
                        withdraw.mutate(r.requestId, {
                          onSuccess: () => toast({ title: 'Request withdrawn' }),
                          onError: onError('Could not withdraw'),
                        })
                      }
                    >
                      Withdraw
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={askAgain.isPending}
                      onClick={() =>
                        askAgain.mutate(r.requestId, {
                          onSuccess: () => toast({ title: 'Asked again' }),
                          onError: onError('Could not ask again'),
                        })
                      }
                    >
                      Ask again
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {requests.hasMore && (
          <Button
            size="sm"
            variant="outline"
            disabled={requests.isFetchingMore}
            onClick={requests.loadMore}
          >
            {requests.isFetchingMore ? 'Loading…' : 'Show more'}
          </Button>
        )}
      </CardContent>
      <StepUpDialog
        open={approving !== null}
        onOpenChange={(open) => !open && setApproving(null)}
        title="Approve this request?"
        description={
          approving
            ? approving.kind === 'group_member'
              ? `${approvingWords} Approving lets them read this label’s documents, each up to their workspace role. The people on the label are told. Confirm it’s you to continue.`
              : `${approvingWords} Approving it lets more people see the document. Confirm it’s you to continue.`
            : undefined
        }
        confirmLabel="Approve"
        onConfirm={async (creds) => {
          if (!approving) return;
          await approveVaultNamedLabelRequest(holderId, approving.requestId, creds);
        }}
        onSuccess={() => {
          toast({ title: 'Approved' });
          setApproving(null);
          void invalidate();
        }}
      />
    </Card>
  );
}

/**
 * One request in plain words. A `group_member` request names the person it
 * would let in (`subjectPrincipalId`) and its group (`groupName`), both from
 * the listing itself; its label only where the caller can see it (the server
 * sends no `labelId` otherwise, so it reads "a label").
 */
function requestWords(
  request: VaultNamedLabelRequestDto,
  labels: readonly VaultNamedLabelSummaryDto[],
  members: readonly VaultHolderMemberDto[],
  myPrincipalId: string | null,
): string {
  const requester = personLabel(members, request.requestedByPrincipalId, myPrincipalId);
  if (request.kind !== 'group_member') return describeRequest(request, labels, requester);
  return describeRequest(request, labels, requester, {
    person: request.subjectPrincipalId
      ? personLabel(members, request.subjectPrincipalId, myPrincipalId)
      : null,
    groupName: request.groupName ?? null,
  });
}

function RequestText({
  request,
  labels,
  members,
  myPrincipalId,
}: {
  request: VaultNamedLabelRequestDto;
  labels: readonly VaultNamedLabelSummaryDto[];
  members: readonly VaultHolderMemberDto[];
  myPrincipalId: string | null;
}) {
  return <>{requestWords(request, labels, members, myPrincipalId)}</>;
}

// ---------------------------------------------------------------------------
// The caller's own drop-box filings: name and time, never content
// ---------------------------------------------------------------------------

function FilingsCard({ holderId }: { holderId: string }) {
  const receipts = useVaultNamedLabelReceipts(holderId);
  if (!receipts.data || receipts.data.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your filings</CardTitle>
        <CardDescription>
          Documents you filed into a drop box. You can’t open them any more; this is your record
          that you filed them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 text-sm">
          {receipts.data.map((r) => (
            <li key={r.receiptId} className="flex flex-wrap justify-between gap-2">
              <span className="text-foreground">{r.documentName}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(r.filedAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
